import React, { useEffect, useRef, useState, useId, useMemo, useCallback } from "react";
import { VideoJS, VideoSegmentUrls } from "./VideoJS/VideoJS.js";
import { SeekbarEvents } from "./SeekbarEvents/SeekbarEvents.js";
import apiClient from "../../../../helpers/api-client.js";
import i18n from "../../../../i18n.js";
import videoUnavailableSvg from "./images/recording_unavailable.svg";
import DownloadRequestModal, { DownloadRequestStatusType } from "./DownloadRequestModal.js";
import {
	screencaptureDisplayStateAtom,
	webcamDisplayStateAtom,
	videoDisplayStateAtom,
	videoEndDateAtom,
} from "./store.js";
import {
	getLastRecordedSpanMs,
	getRecordedCoverage,
	getRecordedCoverageState,
	SessionRecordingTimestamps,
	TIMESTAMP_PLACEHOLDER,
	TimestampsArr,
	VideoEvent,
} from "../../../utilities/useTimestamps.js";
import RecordingEndedSvg from "./images/RecordingEnded.js";
import { useAtomValue } from "jotai";
import { formatEventTimestamp } from "../../ProctoringResultsPage/ProctoringEventsTable";
import DetectionOverlaySettings, { GearIcon } from "./overlay/DetectionOverlaySettings.js";
import styles from "./ProctoringVideoPlayer.module.css";
import {
	VideoControlButton,
	VolumeControls,
	DownloadButton,
	FullScreenButton,
	MoreOptionsButton,
	VideoSpeedButton,
	VideoTimeStamp,
	PlayButton,
} from "./VideoControls/VideoControls.js";
import VideoCard from "./VideoLayout/VideoCard";
import { RecordingType } from "../../../../types/general-types";
import VideoLayout from "./VideoLayout";
import type { ProctoredMediaContainer } from "./hooks";
import SendEmailModal from "./modals/SendEmailModal.js";
import PlayOverlay from "./overlay/PlayOverlay.js";
import VideoControlLayout, { VideoControlButtonWrapper } from "./VideoControls/VideoControlLayout.js";
import {
	QuestionAttemptSeekbar,
	hasAttemptQuestionEvents,
	getCurrentAttemptQuestionNo,
} from "./VideoControls/QuestionAttemptSeekbar.js";

export type ControlOptions = {
	isPlaying: boolean;
	playbackSpeed: number;
	volume: number;
};

export type SessionNote = {
	id: number | string;
	time: number;
	content: string;
	date: Date;
};

export type VideoSettings = {
	controls: boolean;
	responsive: boolean;
	aspectRatio: string;
	fluid: boolean;
	userActions: {
		click: boolean;
	};
	controlBar: {
		playToggle: boolean;
		volumePanel: boolean;
		progressControl: boolean;
		remainingTimeDisplay: boolean;
	};
};

export type RecordingSourceVideoUrls = Record<number, VideoSegmentUrls>;
export type SessionVideoSources = Partial<Record<RecordingType, RecordingSourceVideoUrls>>;

export type ProctoringVideoPlayerProperties = {
	toggleVolume?: () => void;
	isFullscreen: boolean;
	sessionId: string;
	showTimestamps: boolean;
	/**
	 * TODO move to store
	 */
	controlOptions: ControlOptions;
	videos: SessionVideoSources;
	/**
	 * TODO move to store
	 */
	playback: {
		timer: number;
		lastTime: number;
		skip: boolean;
	};
	/**
	 * TODO move to store
	 */
	playbackDispatch: React.Dispatch<
		| {
				type: "play";
				speed: number;
		  }
		| {
				type: "pause";
		  }
		| {
				type: "skip";
				time: number;
		  }
	>;
	/**
	 * TODO move to store
	 */
	startDate: Date;
	/**
	 * TODO move to store
	 */
	duration: number | undefined;
	/**
	 * TODO move to store
	 */
	timeOffset: number;
	/**
	 *  TODO: move to this component
	 */
	setFullscreen: () => void;
	/**
	 *  TODO: move to this component
	 */
	setPlay: () => void;
	/**
	 *  TODO: move to this component
	 */
	setVolume: React.ChangeEventHandler<HTMLInputElement>;
	/**
	 *  TODO: move to this component
	 */
	setSpeed: (value: string) => void;
	/**
	 *  TODO: move to this component
	 */
	showVideoUnavailable: boolean;
	/**
	 *  TODO: move to this component
	 */
	showAudioThumbnail: boolean;
	/**
	 *  TODO: move to this component
	 */
	webcamVideoOptions: VideoSettings | null | undefined;
	/**
	 *  TODO: move to this component
	 */
	screencaptureVideoOptions: VideoSettings | null | undefined;
	onEmailModalChange?: (showing: boolean) => void;
	/**
	 * Refactor out
	 */
	shouldHideCamVid: boolean;
	/**
	 * Refactor out
	 */
	events: VideoEvent[];
	sessionRecordingTimestamps: SessionRecordingTimestamps;
	/**
	 * Refactor out
	 */
	sessionNotes: SessionNote[];
	/**
	 * Refactor out
	 */
	visibleLiveNotes: SessionNote[];
	/**
	 * Refactor out
	 */
	shouldShowDownload: boolean;

	isWebcamAndScreencaptureDeleted?: boolean;
	webcamAndScreencaptureDeleteCause?: string | null;
	setIsDeleteConfirmationModalShown?: (value: "recording" | "roomscan" | undefined) => void;
	hasInstituteEditPermission?: boolean;
	sessionStatus?: string | undefined | null;
	webCamHasError: boolean;
	setWebCamHasError: (value: boolean) => void;
	screenCaptureHasError: boolean;
	setScreenCaptureHasError: (value: boolean) => void;
	selectedProctoringSessionUuid?: string;
	showMachineLearningOverlay?: boolean;
	proctoredMediaContainer: ProctoredMediaContainer;
	/** Institution recording limit in effect for this attempt, if any. */
	sessionRecordingLimitHours?: number | null;
	/**
	 * Ask the parent to re-fetch signed recording URLs after a camera exhausts its own same-URL
	 * retries (see VideoJS's onSourceLoadFailure). Optional so callers that don't wire it up just keep
	 * today's behaviour of latching until the next explicit seek.
	 */
	onRetrySources?: () => void;
};

/**
 * How far short of the limit a recording can end and still count as having reached it.
 *
 * Chunks that never finish uploading leave holes in the final video, so a truncated recording comes
 * out shorter than its limit. More chunks means more holes, so the gap scales with the limit: a
 * 1-hour limit has been seen ending ~8 minutes early. Hence a fraction rather than a fixed amount,
 * plus a floor so the 1-hour minimum still gets useful slack.
 *
 * Widen the fraction to explain more truncated recordings, at the risk of blaming the setting for
 * ones that actually crashed — length is all we have to tell those apart.
 */
const RECORDING_LIMIT_TOLERANCE_FRACTION = 0.2;
const RECORDING_LIMIT_TOLERANCE_FLOOR_MS = 12 * 60 * 1000;

const getRecordingLimitToleranceMs = (limitMs: number): number =>
	Math.max(RECORDING_LIMIT_TOLERANCE_FLOOR_MS, limitMs * RECORDING_LIMIT_TOLERANCE_FRACTION);

/**
 * The institution limit is stored in whole hours; playback works in milliseconds. Keep this in step
 * with the recording service's chunk budget (`3600 * hours / 10` ten-second chunks) — shortening one
 * for local testing and not the other makes every truncated tail misreport.
 */
const MS_PER_HOUR = 60 * 60 * 1000;

function hasSources(sources: RecordingSourceVideoUrls) {
	return Object.values(sources).some((segments) => Object.keys(segments).length > 0);
}

function ProctoringVideoPlayer({
	isFullscreen,
	showTimestamps,
	controlOptions,
	playback,
	playbackDispatch,
	videos,
	startDate,
	duration,
	timeOffset,
	setFullscreen,
	setPlay,
	setVolume,
	setSpeed,
	showVideoUnavailable,
	showAudioThumbnail,
	webcamVideoOptions,
	screencaptureVideoOptions,
	onEmailModalChange,
	events,
	sessionRecordingTimestamps,
	sessionNotes,
	visibleLiveNotes,
	shouldShowDownload,
	toggleVolume,
	isWebcamAndScreencaptureDeleted,
	webcamAndScreencaptureDeleteCause,
	setIsDeleteConfirmationModalShown,
	hasInstituteEditPermission,
	sessionStatus,
	webCamHasError,
	setWebCamHasError,
	screenCaptureHasError,
	setScreenCaptureHasError,
	selectedProctoringSessionUuid,
	sessionId,
	showMachineLearningOverlay = false,
	proctoredMediaContainer,
	sessionRecordingLimitHours,
	onRetrySources,
}: ProctoringVideoPlayerProperties) {
	const videoDisplayState = useAtomValue(videoDisplayStateAtom);
	const videoEndDate = useAtomValue(videoEndDateAtom);

	const [emailModalOpen, setEmailModalOpen] = useState(false);
	const [menuShowing, setMenuShowing] = useState(false);
	const [showVideoSpeed, setShowVideoSpeed] = useState(false);
	const controlContainerRef = useRef<HTMLDivElement>(null);
	const playerContainerRef = useRef<HTMLDivElement>(null);

	const [downloadRequestStatus, setDownloadRequestStatus] = useState<DownloadRequestStatusType>("unsent");

	const [isVideoPlayerOverlayVisible, setIsVideoPlayerOverlayVisible] = useState(true);

	/**
	 * webCamHasError/screenCaptureHasError below are shared across every camera of a given type (there
	 * can be more than one webcam source index, eg. a secondary device camera). Wiring that single flag
	 * straight into every VideoJS instance of that type means one genuinely-broken camera latches every
	 * other, healthy camera of the same type as "Recording Is Unavailable" too. Track per-(type,index)
	 * load errors locally instead, and only fall back to the shared flag for whole-type conditions like
	 * an explicit delete (isWebcamAndScreencaptureDeleted, synced below).
	 */
	const [perCameraLoadErrors, setPerCameraLoadErrors] = useState<Record<string, boolean>>({});
	const setCameraHasErrorFor = useCallback((key: string, value: boolean) => {
		setPerCameraLoadErrors((previous) => {
			if (!value) {
				if (!(key in previous)) return previous;
				const { [key]: _removed, ...rest } = previous;
				return rest;
			}
			if (previous[key]) return previous;
			return { ...previous, [key]: true };
		});
	}, []);
	/**
	 * VideoJS's load effect depends on `setVideoHasError`'s identity to decide whether to re-attempt a
	 * load (see its isNewSrc gating). A closure created fresh per render — as `(value) =>
	 * setCameraHasErrorFor(cameraErrorKey, value)` would be if built inline in the JSX below — changes
	 * identity every render, causing that effect to re-run (and re-register canplaythrough/timeupdate
	 * listeners) on every parent re-render, not just real source/error changes. Cache one stable
	 * function per camera key instead.
	 */
	const cameraErrorSetterCacheRef = useRef<Record<string, (value: boolean) => void>>({});
	useEffect(() => {
		setPerCameraLoadErrors({});
		cameraErrorSetterCacheRef.current = {};
	}, [sessionId]);

	const haveAllRecordingsFinishedPlaying = duration !== undefined && playback.timer >= duration - 0.01;

	useEffect(() => {
		setIsVideoPlayerOverlayVisible(!controlOptions.isPlaying);
	}, [controlOptions.isPlaying]);

	useEffect(() => {
		setWebCamHasError(!!isWebcamAndScreencaptureDeleted);
		setScreenCaptureHasError(!!isWebcamAndScreencaptureDeleted);
	}, [isWebcamAndScreencaptureDeleted]);

	let videoOverlayType: string | undefined;
	if (!screenCaptureHasError && !webCamHasError) {
		videoOverlayType = "both";
	} else if (!screenCaptureHasError && webCamHasError) {
		videoOverlayType = "screencapture";
	} else if (screenCaptureHasError && !webCamHasError) {
		videoOverlayType = "webcam";
	}

	// Note - didn't use videoOverlayType because videoOverlayType would be "both" even if it is only screen recording or only webcam
	const hasScreenRecordSource = videos.screencapture && hasSources(videos.screencapture);
	const hasWebcamSource = videos.webcam && hasSources(videos.webcam);
	// Only show fullscreen buttons if there is both webcam and screen recording AND we are in fullscreen

	const settingsButtonRef = useRef<HTMLButtonElement>(null);
	const [settingsPopoverOpen, setSettingsPopoverOpen] = useState(false);
	const settingsPopoverId = useId();

	useEffect(() => {
		const handleResize = () => {
			setSettingsPopoverOpen(false);
		};

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, [setSettingsPopoverOpen]);

	const videoTemplate = useMemo(
		() =>
			Object.fromEntries(
				Object.entries(sessionRecordingTimestamps).map(([type, sourceTimestamps]) => [
					type as RecordingType,
					Object.keys(sourceTimestamps).length,
				]),
			),
		[sessionRecordingTimestamps],
	);

	const hasAttemptQuestions = useMemo(() => hasAttemptQuestionEvents(events), [events]);
	const attemptQuestionEvents = useMemo(
		() =>
			events.filter(
				(e) =>
					e.type === "assessment" && e.name === "attempt question" && typeof e.details?.metadata?.duration === "number",
			),
		[events],
	);
	const currentQuestionNo = hasAttemptQuestions
		? getCurrentAttemptQuestionNo(attemptQuestionEvents, playback.timer)
		: null;

	const showSeekbarTime = useMemo(() => {
		const isAnySegmentOk =
			Object.values(proctoredMediaContainer.webcam).some((source) =>
				Object.values(source).some((segment) => segment.ok),
			) ||
			Object.values(proctoredMediaContainer.screencapture).some((source) =>
				Object.values(source).some((segment) => segment.ok),
			);

		const hasWebcamOrScreencaptureError = webCamHasError || screenCaptureHasError;
		const isIncompleteSession = sessionStatus === "incomplete";
		return !hasWebcamOrScreencaptureError && (isAnySegmentOk || !isIncompleteSession);
	}, [proctoredMediaContainer, webCamHasError, screenCaptureHasError, sessionStatus]);

	// Whether the playhead has run past the end of media that the institution limit cut short — the
	// one blank stretch we can actually account for. Every other blank (sleep, a crash, failed
	// uploads) is left as it was, since attributing it to policy would be a guess.
	const getIsPastRecordingLimit = useCallback(
		({
			type,
			sourceIndex,
			sourceTimestamps,
			timeMs,
		}: {
			type: RecordingType;
			sourceIndex: number;
			sourceTimestamps: TimestampsArr;
			timeMs: number;
		}): boolean => {
			// `null` means the institution set no limit, `undefined` that this attempt predates the
			// snapshot; neither leaves us anything to attribute a truncated recording to.
			if (sessionRecordingLimitHours == null || (type !== "webcam" && type !== "screencapture")) {
				return false;
			}
			const coverage = getRecordedCoverage({
				segments: proctoredMediaContainer[type]?.[sourceIndex],
				timestamps: sourceTimestamps,
			});
			// Only a tail qualifies: an interior blank stretch is followed by more media, so the
			// recording demonstrably did not stop there.
			if (getRecordedCoverageState(coverage, timeMs) !== "tail") {
				return false;
			}
			const limitMs = sessionRecordingLimitHours * MS_PER_HOUR;
			return getLastRecordedSpanMs(coverage) >= limitMs - getRecordingLimitToleranceMs(limitMs);
		},
		[proctoredMediaContainer, sessionRecordingLimitHours],
	);

	return (
		<>
			<div className={styles.videoContainer} ref={playerContainerRef}>
				{!showVideoUnavailable && (
					<VideoLayout
						videoTemplate={videoTemplate}
						videoPlayers={Object.fromEntries(
							Object.entries(sessionRecordingTimestamps).map(([typeKey, sourceTimestamps]) => {
								if (!sourceTimestamps || Object.keys(sourceTimestamps).length === 0) {
									return [typeKey, []];
								}

								const type = typeKey as RecordingType;
								const sourceUrls = videos[type] ?? {};
								return [
									type,
									Object.entries(sourceUrls).map(([indexKey, sources]) => {
										const sourceIndex = Number(indexKey);
										const isPastRecordingLimit = getIsPastRecordingLimit({
											type,
											sourceIndex,
											sourceTimestamps: sourceTimestamps[sourceIndex] ?? [],
											timeMs: playback.timer,
										});
										const cameraErrorKey = `${type}:${sourceIndex}`;
										const externalHasError = type === "webcam" ? webCamHasError : screenCaptureHasError;
										const cameraHasError = externalHasError || !!perCameraLoadErrors[cameraErrorKey];
										let setCameraHasError = cameraErrorSetterCacheRef.current[cameraErrorKey];
										if (!setCameraHasError) {
											setCameraHasError = (value: boolean) => setCameraHasErrorFor(cameraErrorKey, value);
											cameraErrorSetterCacheRef.current[cameraErrorKey] = setCameraHasError;
										}
										return (
											<VideoCard
												key={indexKey}
												type={type === "webcam" && sourceIndex === 0 && showAudioThumbnail ? "microphone" : type}
												index={Object.keys(sourceUrls).length > 1 ? sourceIndex : undefined}
												onClick={setPlay}
											>
												<VideoJS
													events={events}
													debugging_string={type}
													dispatch={playbackDispatch}
													options={type === "webcam" ? webcamVideoOptions : screencaptureVideoOptions}
													controls={controlOptions}
													startDate={startDate}
													arrStamps={sourceTimestamps[sourceIndex] ?? [TIMESTAMP_PLACEHOLDER]}
													sources={sources}
													videoTimer={playback}
													displayStateAtom={type === "webcam" ? webcamDisplayStateAtom : screencaptureDisplayStateAtom}
													toggledTimestamps={showTimestamps}
													isFullscreen={isFullscreen}
													showAudioThumbnail={type === "webcam" && sourceIndex === 0 && showAudioThumbnail}
													isDeleted={isWebcamAndScreencaptureDeleted}
													deleteCause={webcamAndScreencaptureDeleteCause}
													isIncomplete={sessionStatus === "incomplete"}
													videoHasError={cameraHasError}
													setVideoHasError={setCameraHasError}
													onSourceLoadFailure={onRetrySources}
													selectedProctoringSessionUuid={selectedProctoringSessionUuid}
													showMachineLearningOverlay={
														type === "webcam" && sourceIndex === 0 && showMachineLearningOverlay
													}
													isPastRecordingLimit={isPastRecordingLimit}
												/>
											</VideoCard>
										);
									}),
								];
							}),
						)}
					/>
				)}

				{
					// only show the overlay on the available recording. If both videos are available, show the overlay covering both videos.
					videoOverlayType === "both" && sessionStatus !== "incomplete" && (
						<PlayOverlay
							useVideoEndedOverlay={haveAllRecordingsFinishedPlaying}
							isOverlayVisible={isVideoPlayerOverlayVisible}
							setPlay={setPlay}
						/>
					)
				}

				{videoDisplayState === "ended" && (
					<div style={{ height: "100%" }}>
						<RecordingEndedSvg
							style={{
								height: "100%",
								width: "100%",
							}}
							endDate={videoEndDate ?? new Date(startDate.getTime() + (duration ?? 0))}
						/>
					</div>
				)}

				{videoDisplayState !== "ended" && showVideoUnavailable && (
					<button
						style={{
							height: "100%",
							width: "100%",
							justifyContent: "center",
							alignItems: "center",
							display: "flex",
							background: "none",
							border: "none",
							cursor: "pointer",
							outline: "none",
						}}
						tabIndex={-1}
						onClick={setPlay}
					>
						<img style={{ maxWidth: "100%", height: "100%" }} src={videoUnavailableSvg} alt="Video is unavailable" />
					</button>
				)}
			</div>
			{(videos.screencapture || videos.webcam) && (
				<>
					<QuestionAttemptSeekbar
						events={events}
						curTimeMs={playback.timer}
						durationMs={duration}
						timeOffsetMs={timeOffset}
						inFullscreen={isFullscreen}
						isPlaying={controlOptions.isPlaying}
						onSeekbarChange={(e) => playbackDispatch({ type: "skip", time: e.target.valueAsNumber })}
					>
						<SeekbarEvents
							events={events}
							sessionNotes={sessionNotes}
							liveNotes={visibleLiveNotes}
							dispatch={playbackDispatch}
							duration={duration}
							globaltime={playback.timer / 1000}
							skip={playback.skip}
							offsetGetter={timeOffset}
							isFullscreen={isFullscreen}
						/>
					</QuestionAttemptSeekbar>

					<VideoControlLayout inFullscreen={isFullscreen} controlContainerRef={controlContainerRef}>
						<PlayButton
							inFullscreen={isFullscreen}
							isPlaying={controlOptions.isPlaying}
							onClick={() => {
								// only enable playing if at least one video is available
								if (hasScreenRecordSource || hasWebcamSource) setPlay();
							}}
						/>
						<VolumeControls
							volume={controlOptions.volume}
							showVolumeInput={!isWebcamAndScreencaptureDeleted}
							inFullscreen={isFullscreen}
							onVolumeButtonClick={toggleVolume}
							onVolumeInputChange={setVolume}
						/>
						<VideoTimeStamp inFullscreen={isFullscreen}>
							{!showSeekbarTime
								? "0:00"
								: `${formatEventTimestamp(playback.timer - timeOffset, (duration ?? 0) - timeOffset)}`}
							<span style={{ color: "#FFFFFF99" }}>
								{!showSeekbarTime
									? " / 0:00"
									: ` / ${formatEventTimestamp((duration ?? 0) - timeOffset, (duration ?? 0) - timeOffset)}`}
							</span>
							{currentQuestionNo !== null && (
								<>
									<span aria-hidden="true" style={{ border: "1px solid #676767", margin: "0 5px" }}></span>
									{i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.question", {
										number: currentQuestionNo,
									})}
								</>
							)}
						</VideoTimeStamp>

						<VideoControlButtonWrapper>
							<VideoSpeedButton
								menuShowing={showVideoSpeed}
								setMenuShowing={setShowVideoSpeed}
								playbackSpeed={controlOptions.playbackSpeed}
								controlContainerRef={controlContainerRef}
								onSelectSpeed={(speed) => setSpeed(speed.toString())}
								inFullscreen={isFullscreen}
							/>
							{shouldShowDownload && !(webCamHasError && screenCaptureHasError) && !isFullscreen && (
								<DownloadButton
									inFullscreen={isFullscreen}
									onClick={() => {
										setEmailModalOpen(true);
										onEmailModalChange?.(true);
									}}
								/>
							)}
							{!isFullscreen && (
								<DownloadRequestModal
									status={downloadRequestStatus}
									onClose={() => setDownloadRequestStatus("unsent")}
								/>
							)}

							{(hasScreenRecordSource || hasWebcamSource) &&
								!isWebcamAndScreencaptureDeleted &&
								!isFullscreen &&
								hasInstituteEditPermission &&
								sessionStatus !== "incomplete" && (
									<MoreOptionsButton
										menuShowing={menuShowing}
										setMenuShowing={setMenuShowing}
										inFullscreen={isFullscreen}
										onDeleteRecording={() => setIsDeleteConfirmationModalShown?.("recording")}
									/>
								)}

							{/* Settings button */}
							{showMachineLearningOverlay && (
								<VideoControlButton
									className={styles.settingsButton}
									onClick={() => setSettingsPopoverOpen(true)}
									aria-label={i18n.t(
										"result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.settings",
									)}
									aria-haspopup
									aria-controls={settingsPopoverId}
									ref={settingsButtonRef}
								>
									<GearIcon className={styles.settingsIcon} aria-hidden="true" />
								</VideoControlButton>
							)}

							<FullScreenButton inFullscreen={isFullscreen} onClick={() => setFullscreen()} />
						</VideoControlButtonWrapper>
					</VideoControlLayout>
				</>
			)}

			{/* Settings popover */}
			<DetectionOverlaySettings
				accessibility={{
					id: settingsPopoverId,
				}}
				refs={{
					anchor: settingsButtonRef,
					container: playerContainerRef,
				}}
				state={{
					open: settingsPopoverOpen,
				}}
				hooks={{
					onClose: () => setSettingsPopoverOpen(false),
				}}
			/>
			{emailModalOpen && (
				<SendEmailModal
					onClose={() => {
						setEmailModalOpen(false);
						onEmailModalChange?.(false);
					}}
					onSend={(userEnteredEmail, userEnteredName) => {
						setDownloadRequestStatus("sending");
						void apiClient.download
							.requestProctoringSessionRecordingDownload(sessionId, userEnteredEmail, userEnteredName)
							.then(({ ready, emailDestination }) => {
								setDownloadRequestStatus({ status: "sent", ready, emailDestination });
							})
							.catch((err) => {
								console.error(err);
								setDownloadRequestStatus("failed");
							});
					}}
				/>
			)}
		</>
	);
}

export default ProctoringVideoPlayer;
